/**
 * Referrals Page — Share the Love
 * Payout-lifecycle referral dashboard for clients.
 */

import { useState, useEffect, useRef } from 'react'
import { useAuth } from '@contexts/AuthContext'
import { supabase } from '@lib/supabase'
import { motion } from 'framer-motion'
import {
  DollarSign, Users, Clock, CheckCircle2, XCircle,
  Copy, Check, Send, Heart, AlertCircle, ChevronDown,
  ChevronUp, Sparkles, FileText, Building2,
  ArrowRight, BookOpen, MessageSquare
} from 'lucide-react'
import toast from 'react-hot-toast'

const FUNCTIONS_URL = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL
  || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`
const APP_URL = import.meta.env.VITE_APP_URL || 'https://loveandphotos.com'

const PAYOUT_AMOUNT = 20000 // cents — $200

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDollars(cents) {
  return `$${(cents / 100).toFixed(0)}`
}

function fmtReferralName(fullName, fallbackEmail) {
  if (fullName && fullName.trim() && !fullName.includes('@')) {
    const parts = fullName.trim().split(/\s+/)
    if (parts.length === 1) return parts[0]
    return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`
  }
  return fallbackEmail || '—'
}

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function daysUntilPayout(bookingConfirmedAt) {
  if (!bookingConfirmedAt) return null
  const elapsed = Math.floor((Date.now() - new Date(bookingConfirmedAt).getTime()) / (1000 * 60 * 60 * 24))
  const remaining = 31 - elapsed
  return Math.max(0, remaining)
}

// ── Payout status config ──────────────────────────────────────────────────────

const PAYOUT_CONFIG = {
  pending: {
    label: 'Pending',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Clock,
  },
  success: {
    label: 'Success',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle2,
  },
  completed: {
    label: 'Completed',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: DollarSign,
  },
  void: {
    label: 'Void',
    color: 'bg-gray-100 text-gray-500 border-gray-200',
    icon: XCircle,
  },
}

// ── Referral row card ─────────────────────────────────────────────────────────

function ReferralCard({ referral }) {
  const cfg = PAYOUT_CONFIG[referral.payout_status] ?? PAYOUT_CONFIG.pending
  const Icon = cfg.icon
  const daysLeft = referral.payout_status === 'pending' ? daysUntilPayout(referral.booking_confirmed_at) : null

  // Progress toward 31-day payout window
  const progressDays = (() => {
    if (referral.payout_status !== 'pending' || !referral.booking_confirmed_at) return null
    const elapsed = Math.floor((Date.now() - new Date(referral.booking_confirmed_at).getTime()) / (1000 * 60 * 60 * 24))
    return Math.min(31, elapsed)
  })()

  return (
    <div className="py-3 border-b border-border last:border-0">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">
            {fmtReferralName(referral.referred_client_name, referral.referee_email)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {referral.booking_confirmed_at || referral.sent_at
              ? fmtDate(referral.booking_confirmed_at || referral.sent_at)
              : 'Invite sent'}
            {referral.payout_status === 'pending' && daysLeft !== null && daysLeft > 0 && (
              <span className="ml-2 text-amber-600 font-medium">{daysLeft}d until clears</span>
            )}
            {referral.payout_status === 'pending' && daysLeft === 0 && (
              <span className="ml-2 text-rose-500 font-medium">Clearing soon</span>
            )}
            {referral.payout_status === 'completed' && referral.completed_at && (
              <span className="ml-2">· Paid {fmtDate(referral.completed_at)}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm font-bold text-foreground">
            {fmtDollars(referral.payout_amount ?? PAYOUT_AMOUNT)}
          </span>
          <span className={`inline-flex items-center gap-1 text-xs font-semibold border px-2.5 py-0.5 rounded-full uppercase tracking-wide ${cfg.color}`}>
            <Icon className="w-3 h-3" />
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Progress bar — only for pending referrals with a confirmed booking date */}
      {progressDays !== null && (
        <div className="mt-2">
          <div className="w-full h-1.5 bg-rose-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-rose-400 rounded-full transition-all duration-500"
              style={{ width: `${(progressDays / 31) * 100}%` }}
            />
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5 text-right">
            Day {progressDays} of 31
          </p>
        </div>
      )}
    </div>
  )
}

// ── Coupon code box ───────────────────────────────────────────────────────────

function CouponCodeBox({ initialCode, onCodeSaved }) {
  const [code, setCode]           = useState(initialCode || '')
  const [editing, setEditing]     = useState(!initialCode)
  const [checking, setChecking]   = useState(false)
  const [available, setAvailable] = useState(null)
  const [saving, setSaving]       = useState(false)
  const [copied, setCopied]       = useState(false)
  const [copiedLink, setCopiedLink] = useState(false)
  const debounceRef               = useRef(null)

  // Sync display state when initialCode arrives after mount
  useEffect(() => {
    if (initialCode) {
      setCode(initialCode)
      setEditing(false)
    }
  }, [initialCode])

  const shareUrl = initialCode ? `https://loveandphotos.com/?code=${initialCode}` : null

  const handleCodeChange = (val) => {
    const normalized = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20)
    setCode(normalized)
    setAvailable(null)
    if (normalized.length >= 6) {
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(async () => {
        setChecking(true)
        try {
          const { data: { session } } = await supabase.auth.getSession()
          const res = await fetch(`${FUNCTIONS_URL}/referrals/check-code?code=${normalized}`, {
            headers: { Authorization: `Bearer ${session.access_token}` }
          })
          const data = await res.json()
          setAvailable(data.available)
        } catch { setAvailable(null) } finally { setChecking(false) }
      }, 400)
    }
  }

  const handleSave = async () => {
    if (!code || code.length < 6 || available === false) return
    setSaving(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${FUNCTIONS_URL}/referrals/save-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ code })
      })
      const data = await res.json()
      if (!res.ok) { toast.error(data.error || 'Failed to save'); return }
      toast.success('Invite code saved!')
      setEditing(false)
      onCodeSaved(data.code)
    } catch { toast.error('Failed to save code') } finally { setSaving(false) }
  }

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-rose-400" />
        <h2 className="text-base font-semibold text-foreground">Your Referral Code</h2>
      </div>

      {!editing && initialCode ? (
        <div className="space-y-2.5">
          <p className="text-xs text-muted-foreground">Share this code — friends enter it at checkout.</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-muted rounded-lg px-4 py-2.5 font-mono text-xl font-black text-foreground tracking-widest text-center border border-border">
              {initialCode}
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(initialCode); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
              className="p-2.5 rounded-lg border border-border hover:bg-muted transition-colors"
              title="Copy code"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
            </button>
          </div>
          {shareUrl && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => { navigator.clipboard.writeText(shareUrl); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000) }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-500 hover:text-rose-600 transition-colors"
              >
                <Copy className="w-3 h-3" />
                {copiedLink ? 'Copied!' : 'Copy link'}
              </button>
              <a
                href={`sms:?body=${encodeURIComponent(`Hey! I'm inviting you to check out Love & Photos for your photography needs. Use my code ${initialCode} at checkout: ${shareUrl}`)}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors sm:hidden"
              >
                <MessageSquare className="w-3 h-3" />
                Text a friend
              </a>
            </div>
          )}
          <button onClick={() => setEditing(true)} className="text-xs text-muted-foreground hover:text-foreground underline">
            Change code
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Create a personal code. Min 6 characters, uppercase letters and numbers only.
          </p>
          <div className="relative">
            <input
              type="text"
              value={code}
              onChange={e => handleCodeChange(e.target.value)}
              placeholder="e.g. SARAH25"
              maxLength={20}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background font-mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-rose-400"
            />
            {code.length >= 6 && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {checking
                  ? <div className="w-4 h-4 border-2 border-rose-300 border-t-rose-500 rounded-full animate-spin" />
                  : available === true
                    ? <Check className="w-4 h-4 text-green-600" />
                    : available === false
                      ? <XCircle className="w-4 h-4 text-red-500" />
                      : null}
              </div>
            )}
          </div>
          {code.length >= 6 && available === false && (
            <p className="text-xs text-red-600">That code is already taken. Try a different one.</p>
          )}
          {code.length > 0 && code.length < 6 && (
            <p className="text-xs text-muted-foreground">{6 - code.length} more characters needed</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || code.length < 6 || available === false || checking}
              className="flex-1 px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-semibold hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving…' : 'Save Code'}
            </button>
            {initialCode && (
              <button
                onClick={() => { setEditing(false); setCode(initialCode) }}
                className="px-4 py-2 border border-border rounded-lg text-sm text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Program Rules sidebar ─────────────────────────────────────────────────────

function ProgramRules() {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="w-4 h-4 text-rose-400" />
        <h2 className="text-base font-semibold text-foreground">Program Rules</h2>
      </div>
      <ul className="space-y-2.5 mb-4">
        {[
          `$200 USD per successful booking.`,
          `Booking must be confirmed with a signed contract and retainer.`,
          `Payouts are issued approximately 31–38 days after booking, provided payment information has been fully submitted.`,
          `W-9 and banking information are required for participation via QBO Online. An onboarding invitation will be sent to your email upon your first successful referral.`,
          `Referrals are tracked via your unique invite code, which must be applied at checkout at the time of a new booking to initiate the referral credit process.`,
        ].map((rule, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
            <span className="w-4 h-4 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5 text-[10px] font-bold">
              {i + 1}
            </span>
            {rule}
          </li>
        ))}
      </ul>
      <a
        href="/terms#section-15"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-500 hover:text-rose-600 transition-colors"
      >
        <FileText className="w-3.5 h-3.5" />
        Terms &amp; Conditions
      </a>
    </div>
  )
}

// ── Invite Friends box ────────────────────────────────────────────────────────

function InviteFriends({ onSent }) {
  const [tab, setTab]       = useState('email')
  const [email, setEmail]   = useState('')
  const [phone, setPhone]   = useState('')
  const [sending, setSending] = useState(false)

  const handleSendEmail = async (e) => {
    e.preventDefault()
    const trimmed = email.trim().toLowerCase()
    if (!trimmed) return
    setSending(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${FUNCTIONS_URL}/referrals/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ referee_email: trimmed })
      })
      const json = await res.json()
      if (!res.ok) { toast.error(json.error || 'Something went wrong'); return }
      toast.success('Invite sent!')
      setEmail('')
      onSent()
    } catch { toast.error('Failed to send invite') } finally { setSending(false) }
  }

  const handleSendSms = async (e) => {
    e.preventDefault()
    const trimmed = phone.trim()
    if (!trimmed) return
    setSending(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${FUNCTIONS_URL}/referrals/send-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ phone: trimmed })
      })
      const json = await res.json()
      if (!res.ok) { toast.error(json.error || 'Something went wrong'); return }
      toast.success('Text invite sent!')
      setPhone('')
      onSent()
    } catch { toast.error('Failed to send text invite') } finally { setSending(false) }
  }

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Send className="w-4 h-4 text-muted-foreground" />
        <h2 className="text-base font-semibold text-foreground">Invite Friends</h2>
      </div>

      {/* Tab toggle */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg mb-4">
        <button
          onClick={() => setTab('email')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            tab === 'email' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Send className="w-3 h-3" />
          Email
        </button>
        <button
          onClick={() => setTab('sms')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            tab === 'sms' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <MessageSquare className="w-3 h-3" />
          Text
        </button>
      </div>

      {tab === 'email' ? (
        <form onSubmit={handleSendEmail} className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="friend@example.com"
            required
            className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-rose-400"
          />
          <button
            type="submit"
            disabled={sending || !email.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-semibold hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            <Send className="w-3.5 h-3.5" />
            {sending ? 'Sending…' : 'Send'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleSendSms} className="flex gap-2">
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="(555) 867-5309"
            required
            className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-rose-400"
          />
          <button
            type="submit"
            disabled={sending || !phone.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-500 text-white rounded-lg text-sm font-semibold hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {sending ? 'Sending…' : 'Send'}
          </button>
        </form>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

const Referrals = () => {
  const { user } = useAuth()
  const [data, setData]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [sentOpen, setSentOpen] = useState(false)

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return
      const res = await fetch(`${FUNCTIONS_URL}/referrals/mine`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      })
      const text = await res.text()
      let json
      try { json = JSON.parse(text) } catch {
        throw new Error('Edge function returned an unexpected response.')
      }
      if (!res.ok) throw new Error(json.error || 'Failed to load')
      setData(json)
    } catch (e) {
      console.error(e)
      toast.error('Failed to load referral data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-10 space-y-4">
        {[1,2,3].map(i => <div key={i} className="h-28 bg-muted rounded-xl animate-pulse" />)}
      </div>
    )
  }

  const { referrals = [], summary = {}, payee_info = {}, referral_code } = data ?? {}

  const payoutReferrals = referrals.filter(r => r.payout_status)
  const successGroup    = payoutReferrals.filter(r => r.payout_status === 'success')
  const pendingGroup    = payoutReferrals.filter(r => r.payout_status === 'pending')
  const completedGroup  = payoutReferrals.filter(r => r.payout_status === 'completed')
  const voidGroup       = payoutReferrals.filter(r => r.payout_status === 'void')

  const readyAmount = successGroup.reduce((s, r) => s + (r.payout_amount ?? PAYOUT_AMOUNT), 0)
  const lifetimeEarned = summary.lifetime_earned_cents ?? 0
  const pendingCount = summary.counts?.pending ?? 0
  const successCount = summary.counts?.success ?? 0
  const hasQboAction = pendingCount > 0 || successCount > 0

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-5"
      >
        {/* ── Header Banner ───────────────────────────────────────────────── */}
        <div className="relative rounded-2xl overflow-hidden border border-rose-100 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-white via-rose-50/60 to-pink-50" />
          <div className="absolute top-0 right-0 w-48 h-48 bg-rose-200/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-32 h-32 bg-pink-200/30 rounded-full blur-2xl" />

          <div className="relative flex items-stretch">
            <div className="flex-1 px-8 pt-8 pb-6">
              <h1 className="text-5xl font-black text-gray-900 leading-none tracking-tight">
                Share the Love
              </h1>
              <p className="text-3xl font-bold italic text-rose-500 leading-tight mt-1" style={{ fontFamily: 'Georgia, serif' }}>
                Referral Program<span className="not-italic">♡</span>
              </p>
            </div>
            <div className="relative w-56 shrink-0 hidden sm:block">
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 176 160" fill="none">
                <path d="M88 130C50 102 18 72 18 46C18 26 33 12 52 12C65 12 76 19 88 34C100 19 111 12 124 12C143 12 158 26 158 46C158 72 126 102 88 130Z" stroke="#f9a8b4" strokeWidth="2" strokeDasharray="6 4" fill="none" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-36 h-32 mt-2">
                  <div className="absolute top-1 right-1 w-24 h-28 rounded-xl shadow-md rotate-6 overflow-hidden bg-rose-100 border-2 border-white">
                    <img src="/assets/referals/ref_image_2.jpg" alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute bottom-1 left-1 w-24 h-28 rounded-xl shadow-md -rotate-3 overflow-hidden bg-pink-100 border-2 border-white">
                    <img src="/assets/referals/ref_image_1.jpg" alt="" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative border-t border-rose-100 bg-white/50 px-8 py-4 flex items-center justify-between gap-4">
            {[
              { n: 1, text: 'Create your unique code', highlight: null },
              { n: 2, text: 'Share your code', highlight: null },
              { n: 3, text: 'Friends book and ', highlight: 'you earn $200!' },
            ].map(({ n, text, highlight }, i) => (
              <div key={n} className="flex items-center gap-4 flex-1">
                {i > 0 && <span className="text-xl font-light text-rose-200 shrink-0">·</span>}
                <div className="flex items-center gap-2.5 whitespace-nowrap">
                  <div className="w-8 h-8 rounded-full bg-rose-500 flex items-center justify-center shrink-0 shadow-sm">
                    <span className="text-sm font-black text-white">{n}</span>
                  </div>
                  <span className="text-sm text-gray-600">
                    <span className="text-base font-black text-gray-900 tracking-tight">STEP {n}:</span>{' '}
                    {text}
                    {highlight && <span className="text-rose-500 font-black">{highlight}</span>}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Stats row ───────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-center">
            <p className="text-2xl font-black text-rose-600">{fmtDollars(readyAmount)}</p>
            <p className="text-xs font-medium text-rose-500 mt-0.5">Payment Ready</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <p className="text-2xl font-black text-foreground">{fmtDollars(lifetimeEarned)}</p>
            <p className="text-xs font-medium text-muted-foreground mt-0.5">Lifetime Earnings</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <p className="text-2xl font-black text-foreground">{referrals.length}</p>
            <p className="text-xs font-medium text-muted-foreground mt-0.5">Total Referrals</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <p className="text-2xl font-black text-foreground">{pendingGroup.length + successGroup.length + completedGroup.length}</p>
            <p className="text-xs font-medium text-muted-foreground mt-0.5"># Booked</p>
          </div>
        </div>

        {/* ── Two-column main content ──────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

          {/* Left: code + referrals */}
          <div className="lg:col-span-3 space-y-4">
            <CouponCodeBox
              initialCode={referral_code}
              onCodeSaved={(newCode) => setData(prev => ({ ...prev, referral_code: newCode }))}
            />

            {/* Your Referrals */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h2 className="text-base font-semibold text-foreground mb-3">Your Referrals</h2>
              {payoutReferrals.length === 0 && referrals.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No referrals yet — share your code to start earning.</p>
              ) : (
                <div className="max-h-[520px] overflow-y-auto space-y-4 pr-1">

                  {/* Pending */}
                  {pendingGroup.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-amber-600 mb-2">Pending</p>
                      <div className="divide-y divide-border">
                        {pendingGroup.map(r => <ReferralCard key={r.id} referral={r} />)}
                      </div>
                    </div>
                  )}

                  {/* Success */}
                  {successGroup.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-green-600 mb-2">Success</p>
                      <div className="divide-y divide-border">
                        {successGroup.map(r => <ReferralCard key={r.id} referral={r} />)}
                      </div>
                    </div>
                  )}

                  {/* Completed */}
                  {completedGroup.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-blue-600 mb-2">Completed</p>
                      <div className="divide-y divide-border">
                        {completedGroup.map(r => <ReferralCard key={r.id} referral={r} />)}
                      </div>
                    </div>
                  )}

                  {/* Void */}
                  {voidGroup.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Void</p>
                      <div className="divide-y divide-border">
                        {voidGroup.map(r => <ReferralCard key={r.id} referral={r} />)}
                      </div>
                    </div>
                  )}

                  {/* Invites sent (no payout_status yet) — collapsible */}
                  {referrals.filter(r => !r.payout_status).length > 0 && (
                    <div className="border border-border rounded-lg overflow-hidden">
                      <button
                        onClick={() => setSentOpen(o => !o)}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-muted/40 hover:bg-muted/70 transition-colors"
                      >
                        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                          Sent ({referrals.filter(r => !r.payout_status).length})
                        </span>
                        {sentOpen
                          ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                          : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                        }
                      </button>
                      {sentOpen && (
                        <div className="max-h-[260px] overflow-y-auto divide-y divide-border px-3">
                          {referrals.filter(r => !r.payout_status).map(r => <ReferralCard key={r.id} referral={r} />)}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              )}
            </div>
          </div>

          {/* Right: Invite Friends + Program Rules + QBO Payout Setup */}
          <div className="lg:col-span-2 space-y-4">
            <InviteFriends onSent={loadData} />

            <ProgramRules />

            {/* QBO / Payout Setup — shown when they have active referrals */}
            {hasQboAction && (
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <h2 className="text-base font-semibold text-foreground">Payout Setup</h2>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Both steps must be completed to receive your payout. Contact{' '}
                  <a href="mailto:support@loveandphotos.com" className="text-rose-500 underline">support</a> if you need to update your information.
                </p>
                <div className="space-y-2">
                  {payee_info.qbo_info_sent && (
                    <div className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-green-50 border border-green-200">
                      <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">QBO Onboarding Sent</p>
                        <p className="text-xs text-muted-foreground">
                          Billing has sent QBO onboarding info to your email — fill out the W-9 and banking details there to receive your reward.
                        </p>
                      </div>
                    </div>
                  )}
                  {[
                    {
                      icon: FileText,
                      label: 'W-9 Form',
                      done: payee_info.w9_submitted,
                      doneText: 'Received ✓',
                      pendingText: 'Not yet received — submit via the QBO onboarding email from billing',
                    },
                    {
                      icon: Building2,
                      label: 'Banking Info',
                      done: payee_info.banking_info_added,
                      doneText: 'On file ✓',
                      pendingText: 'Not yet on file — enter via QBO once you complete onboarding',
                    },
                  ].map(({ icon: Icon, label, done, doneText, pendingText }) => (
                    <div key={label} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${done ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-100'}`}>
                      {done
                        ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                        : <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />}
                      <div>
                        <p className="text-sm font-medium text-foreground">{label}</p>
                        <p className="text-xs text-muted-foreground">{done ? doneText : pendingText}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border leading-relaxed">
                  Referrals in Success status cannot be paid out until the QBO status is marked as Complete for both the W9 and Banking Information, at which point the payout can be initiated and processed within the standard 7–10 business day window. Completion status may take up to 48 hours to update.
                </p>
              </div>
            )}
          </div>
        </div>

      </motion.div>
    </div>
  )
}

export default Referrals
